<script setup>
import Icon from 'next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { defineProps, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { currencyFormatter } from 'next/message/bubbles/Lazada/helper/formatter.js';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
  currentChat: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const toggleExpandOrderIds = ref({});
const store = useStore();

function toggle(orderId) {
  toggleExpandOrderIds.value[orderId] = !toggleExpandOrderIds.value[orderId];
}

const statusText = computed(() => {
  const status = props.order.status || '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
});

const statusColor = computed(() => {
  const statusColors = {
    unpaid: 'text-red-700',
    delivered: 'text-green-700',
    packed: 'text-woot-700',
    shipped: 'text-woot-700',
    canceled: 'text-red-700',
    returned: 'text-woot-700',
    shipped_back_success: 'text-red-700',
  };
  return statusColors[props.order.status] || 'text-woot-700';
});

const statusIconCode = computed(() => {
  const statusIcons = {
    unpaid: 'i-lucide-shopping-bag',
    delivered: 'i-lucide-check-circle',
    packed: 'i-inbox-unarchive-line',
    shipped: 'i-lucide-truck',
    canceled: 'i-ri-alert-line',
    returned: 'i-inbox-unarchive-line',
    shipped_back_success: 'i-inbox-unarchive-line',
  };
  return statusIcons[props.order.status] || 'i-lucide-shopping-cart';
});

function onCopyToClipboard(attributeValue) {
  if (!attributeValue) return;
  copyTextToClipboard(attributeValue);
  useAlert(t('GENERAL.COPY_SUCCESSFUL'));
}

const priceFormatter = price => {
  return currencyFormatter(price || 0);
};

const sendOrder = order => {
  store.dispatch('lazada/sendOrderMessage', {
    conversationID: props.currentChat.id,
    orderID: order.orderId,
  });
};
</script>

<template>
  <div class="header flex flex-col text-xs pb-1">
    <div class="flex justify-between items-center">
      <span class="flex-none">
        <Icon class="text-lg" :class="statusColor" :icon="statusIconCode" />
      </span>
      <span class="flex-auto ms-1 font-bold capitalize" :class="statusColor">
        {{ statusText }}
      </span>
    </div>
    <div
      class="text-xs flex items-center gap-2 border-b border-dashed border-slate-200 dark:border-slate-700"
    >
      <p class="flex items-center gap-2">
        <span class="font-semibold">{{
          t('CONVERSATION.LAZADA.ORDER.ORDER_NUMBER')
        }}</span>
        <span class="text-woot-700">{{ order.orderNumber }}</span>
      </p>
      <button class="p-0" @click="() => onCopyToClipboard(order.orderNumber)">
        <Icon icon="i-lucide-copy" class="text-woot-700" />
      </button>
    </div>
    <ul class="list-none">
      <li
        v-for="(item, index) in order.orderItems"
        v-show="toggleExpandOrderIds[order.id] || index < 2"
        :key="item.itemId"
      >
        <div class="flex items-start gap-4 p-4">
          <div
            class="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0"
          >
            <img
              :src="item.meta.image_url"
              alt="Item Image"
              class="w-full h-full min-w-full min-h-full object-cover"
            />
          </div>
          <div class="flex flex-col justify-between min-w-0">
            <span
              v-tooltip.top="item.itemName"
              class="text-xs font-semibold text-gray-800 line-clamp-2"
            >
              {{ item.itemName }}
            </span>
            <span class="text-xs text-gray-500">{{ item.itemSku }}</span>
            <span class="text-sm font-medium text-woot-600">{{
              priceFormatter(item.price)
            }}</span>
          </div>
        </div>
      </li>
    </ul>
    <div v-if="order.orderItems.length > 2" class="text-center px-4 pb-2">
      <button class="text-sm text-woot-600" @click="toggle(order.id)">
        {{
          toggleExpandOrderIds[order.id]
            ? t('CONVERSATION.LAZADA.ORDER.VIEW_LESS')
            : t('CONVERSATION.LAZADA.ORDER.VIEW_MORE')
        }}
      </button>
    </div>
    <div
      class="pt-3 border-dashed border-t border-slate-200 dark:border-slate-700"
    >
      <p class="flex justify-between text-sm">
        <span class="font-semibold">{{
          t('CONVERSATION.LAZADA.ORDER.ORDERED_AT')
        }}</span>
        <span>{{ order.orderedAt }}</span>
      </p>
      <p v-if="order.cod" class="flex justify-between text-sm items-center">
        <span class="font-semibold">{{
          t('CONVERSATION.LAZADA.ORDER.PAYMENT_METHOD')
        }}</span>
        <span>{{ t('CONVERSATION.LAZADA.ORDER.COD') }}</span>
      </p>
      <p class="flex justify-between text-sm items-center">
        <span class="font-semibold">{{
          t('CONVERSATION.LAZADA.ORDER.TOTAL_PRICE')
        }}</span>
        <span class="font-semibold text-lg text-woot-600">{{
          priceFormatter(order.totalAmount)
        }}</span>
      </p>
    </div>
    <div class="flex-auto text-end mt-2">
      <button
        class="ms-2 text-white bg-woot-600 rounded-md py-1 px-5 text-sm hover:bg-woot-400 cursor-pointer"
        @click="sendOrder(order)"
      >
        {{ t('CONVERSATION.LAZADA.SEND') }}
      </button>
    </div>
  </div>
</template>
